import { createSplashScreenPayload } from "@store/user/user-slice-helper";
import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { SphereDashboardAPITypes } from "@stellar/api-logic";
import { BaseCoreApiClientProps } from "@store/store-types";
import { RootState } from "@store/store-helper";
import { BaseUserProps } from "@custom-types/member-types";
import { getCurrentUserProfileInfos } from "@api/core-api-utils";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { BaseCompanyIdProps } from "@custom-types/base-types";

/** Fetches information of current logged in user */
export const fetchCurrentUser = createAsyncThunk<
  FaroCoreApiTypes.CompanyMemberBase,
  BaseCompanyIdProps,
  { state: RootState }
>("user/fetchCurrentUser", async ({ companyId }, { getState }) => {
  const {
    app: { isDevModeEnabled },
  } = getState();

  if (!companyId) {
    throw new Error("No companyId was given to fetchCurrentUser");
  }

  try {
    const data = await getCurrentUserProfileInfos(companyId, isDevModeEnabled);
    return data;
  } catch (error) {
    throw new Error(getErrorDisplayMarkup(error));
  }
});

/**
 * Set the FueFlags. The provided payload will be set as FUE flags directly.
 * It will not be added to the existed FUE flags in backend
 */
export const setFueFlags = createAsyncThunk<
  {
    updatedValue: string;
  },
  BaseCoreApiClientProps,
  { state: RootState }
>("user/setFueFlags", async ({ coreApiClient }, { getState }) => {
  const {
    user: { splashScreens },
  } = getState();
  const fueFlags = createSplashScreenPayload(splashScreens);

  try {
    return await coreApiClient.V3.SDB.setUserFueFlags(fueFlags);
  } catch (error) {
    throw new Error(getErrorDisplayMarkup(error));
  }
});

/** Logs out the current user by calling the logout route. Does not take care of redirection afterwards */
export const logoutUser = createAsyncThunk<void, BaseCoreApiClientProps>(
  "user/logoutUser",
  async ({ coreApiClient }): Promise<void> => {
    try {
      await coreApiClient.V3.SDB.logoutUser();
    } catch (error) {
      throw new Error(getErrorDisplayMarkup(error));
    }
  }
);

/** Props to update the current user profile */
type UpdateCurrentUserProfileProps = BaseUserProps &
  BaseCoreApiClientProps &
  Pick<SphereDashboardAPITypes.SetMetaUserProfilePayload, "password">;

/** Update information of current logged in user */
export const updateCurrentUserProfile = createAsyncThunk<
  SphereDashboardAPITypes.IUserDetails,
  UpdateCurrentUserProfileProps
>(
  "user/updateCurrentUserProfile",
  async ({ coreApiClient, user, password }) => {
    const { name, lastName, userId, mailAddress, profileImageUrl } = user;

    if (!name || !lastName || !mailAddress || !userId) {
      throw new Error("The information to update profile is incomplete");
    }
    try {
      const data = await coreApiClient.V3.SDB.setMetaUserProfile({
        name,
        lastName,
        userId,
        mailAddress,
        profileImageUrl,
        password,
      });
      return data;
    } catch (error) {
      throw new Error(getErrorDisplayMarkup(error));
    }
  }
);

/** Fetches information of current logged in user, extra details like login method, SSO provider */
export const getLoggedInUser = createAsyncThunk<
  SphereDashboardAPITypes.IGetLoggedInUserResponse,
  BaseCoreApiClientProps,
  {
    state: RootState;
  }
>("user/getLoggedInUser", async ({ coreApiClient }) => {
  try {
    const response = await coreApiClient.V3.SDB.getLoggedInUser();
    // We need the full response to remind users to verify email address.
    // To determine unconfirmed email status and code property in response will be used.
    return response;
  } catch (error) {
    throw new Error(getErrorDisplayMarkup(error));
  }
});
